import { Box, Button, Flex, Input, ListItem, SimpleGrid, Text, UnorderedList } from '@chakra-ui/react';
import { ResistomapSelect } from '@resistapp/client/components/forms/resistomap-select';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { RThirdTitle } from '@resistapp/client/components/headers/r-third-title';
import { MultiOptionConfirmModal } from '@resistapp/client/components/modals/multi-option-confirmation-modal';
import { AddOrganisationAccessDropdown } from '@resistapp/client/components/selects/add-project-access-dropdown';
import { GridView } from '@resistapp/client/components/shared/layout';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import {
  usePatchProjectImages,
  usePatchProjectLevels,
  usePatchProjectName,
  usePatchProjectXAxisMode,
  useProjects,
} from '@resistapp/client/hooks/api';
import { useToastWithDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import {
  PartialVolumeNormalisationConfirmation,
  zPartialVolumeNormalisationConfirmation,
} from '@resistapp/common/api-types';
import { isAdmin, resistomapOrganisationId } from '@resistapp/common/features';
import {
  availableFlags,
  FullProject,
  ProjectSampleAction,
  ProjectSamplesAccessStrategy,
  ProjectType,
  SmartChipFlag,
  XAxisMode,
} from '@resistapp/common/types';
import type { GroupBase } from 'chakra-react-select';
import { isAxiosError } from 'axios';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getZoomableLevelsOptions,
  getZoomableLevelsValues,
  LevelOption,
  OrganisationAccessListItem,
  type ProjectSampleAccessInfo,
  SampleManagementForm,
  changeAdminLevels as utilsChangeAdminLevels,
  type LevelsWithZoomableAreasOption,
} from './admin-section-utils';
import { buildReproducibilityHref, buildSelectedIdenticalSamplesReproState } from './reproducibility-link-utils';
// import { getExtraFilenames } from './download-and-link-section';

// Deep deletion previously covered removing samples and sampling points. Disabled (October 2025) but kept here for reference:
// export type ProjectDeletionMode = 'deep' | 'shallow-keep' | 'shallow-revoke';
export type ProjectDeletionMode = 'shallow-keep' | 'shallow-revoke';

interface Props {
  project: FullProject;
  triggerDelete: (mode: ProjectDeletionMode) => Promise<void>;
}

interface SampleAccessModalOption {
  key: string;
  label: string;
  description?: ReactNode;
  strategy: ProjectSamplesAccessStrategy;
  colorScheme?: string;
  variant?: string;
}

interface SampleAccessModalConfig {
  title: string;
  message?: ReactNode;
  options: SampleAccessModalOption[];
}

export function AdminSection({ project, triggerDelete }: Props) {
  const { adminFeatures } = useResearchContext();
  const { refetchData: refetchProject, availableXAxisModes } = useSampleDataContext();
  const { user } = useUser();
  const { data: projects } = useProjects();
  const { data: imageSaved } = usePatchProjectImages(project.id);
  const patchName = usePatchProjectName(project.id, refetchProject);
  const patchLevels = usePatchProjectLevels(project.id);
  const patchXAxisMode = usePatchProjectXAxisMode(project.id, refetchProject);
  const showToast = useToastWithDebounce();

  const [projectName, setProjectName] = useState(project.name);
  const [levelsWithZoomableAreasOptions, setLevelsWithZoomableAreasOptions] = useState<LevelOption[]>(
    getZoomableLevelsValues(project.levelsWithZoomableAreas),
  );
  const [action, setAction] = useState<ProjectSampleAction>(ProjectSampleAction.ADD);
  const [actionProjectId, setActionProjectId] = useState<number | undefined>();
  const [pristineProjectSamples, setPristineProjectSamples] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sampleAccessModal, setSampleAccessModal] = useState<SampleAccessModalConfig | null>(null);
  const [partialVolumeConfirmation, setPartialVolumeConfirmation] = useState<{
    accessStrategy: ProjectSamplesAccessStrategy;
    payload: PartialVolumeNormalisationConfirmation;
  } | null>(null);

  // const plotFileNames = getExtraFilenames(project);

  const allLevelsWithZoomableAreasOptions: LevelsWithZoomableAreasOption[] = useMemo(
    () => getZoomableLevelsOptions(project.originalLevelsWithZoomableAreas),
    [project.originalLevelsWithZoomableAreas],
  );

  // Build X-Axis mode options based on what is actually available in the project
  const xAxisModeOptions = useMemo<XAxisModeOption[]>(
    () =>
      availableXAxisModes.map(mode => ({
        value: mode,
        label: getXAxisModeLabel(mode),
      })),
    [availableXAxisModes],
  );

  const flagOptions = useMemo(
    () => [{ label: 'None', value: '' }, ...availableFlags.map(f => ({ label: f, value: f }))],
    [],
  );

  const totalProjectSamples = useMemo(
    () => Object.values(project.samplesByUID).reduce((count, samples) => count + samples.length, 0),
    [project.samplesByUID],
  );

  const projectSamplingPointCount = useMemo(() => {
    const samplingPointIds = new Set<number>();
    Object.values(project.samplesByUID).forEach(samples => {
      samples.forEach(sample => {
        const samplingPointId = sample.samplingPoint.id;
        samplingPointIds.add(samplingPointId);
      });
    });
    return samplingPointIds.size;
  }, [project.samplesByUID]);

  const projectSampleLabel = useMemo(() => {
    const noun = totalProjectSamples === 1 ? 'sample' : 'samples';
    return `${totalProjectSamples} ${noun}`;
  }, [totalProjectSamples]);

  const organisationsWithAccess = useMemo(
    () => adminFeatures.organisationsWithAccess.filter(org => org.id !== resistomapOrganisationId),
    [adminFeatures.organisationsWithAccess],
  );

  const samplingPointLabel = useMemo(() => {
    const noun = projectSamplingPointCount === 1 ? 'sampling point' : 'sampling points';
    return `${projectSamplingPointCount} ${noun}`;
  }, [projectSamplingPointCount]);

  const projectSampleAccessInfo = useMemo<ProjectSampleAccessInfo[]>(() => {
    const seenSampleIds = new Set<number>();
    const info: ProjectSampleAccessInfo[] = [];
    Object.values(project.samplesByUID).forEach(samples => {
      samples.forEach(sample => {
        if (seenSampleIds.has(sample.id)) {
          return;
        }
        seenSampleIds.add(sample.id);
        info.push({
          sampleId: sample.id,
          samplingPointId: sample.samplingPointId,
        });
      });
    });
    return info;
  }, [project.samplesByUID]);

  const showToastError = useCallback(
    (title: string, description: string) => {
      showToast({
        title,
        description,
        status: 'error',
        duration: 30000,
      });
    },
    [showToast],
  );

  // const onDrop = useCallback(
  //   (files: FileWithPath[]) => {
  //     for (const file of files) {
  //       if (!file.name.endsWith('.pdf') && !file.name.endsWith('.csv')) {
  //         alert(`Please convert ${file.name} into pdf or csv format.`);
  //         return;
  //       }
  //     }
  //     patchImages(files);
  //   },
  //   [patchImages],
  // );

  useEffect(() => {
    if (imageSaved) {
      window.location.reload();
    }
  }, [imageSaved]);

  const targetProjectId = action === ProjectSampleAction.REMOVE ? project.id : actionProjectId;
  const selectedSamples = useMemo(
    () =>
      Object.values(project.focusedByUID || {})
        .flat()
        .map(s => s.id),
    [project.focusedByUID],
  );

  const reproducibilityHref = useMemo(() => {
    const uniqueIds = Array.from(new Set(selectedSamples));
    if (uniqueIds.length === 0) {
      return '/reproducibility';
    }
    // Seed set "A" with the selected samples; the user adds further sets/columns in the tool. Legacy `pair=` links
    // keep working via the reproducibility view's legacy URL parser.
    return buildReproducibilityHref({
      mode: 'sets',
      setLabels: ['A', 'B'],
      groups: uniqueIds.map(id => [id, null]),
    });
  }, [selectedSamples]);

  const selectedIdenticalSamplesReproState = useMemo(
    () => buildSelectedIdenticalSamplesReproState(project.focusedByUID, project.chips),
    [project.chips, project.focusedByUID],
  );
  const selectedIdenticalSamplesReproHref = useMemo(
    () => buildReproducibilityHref(selectedIdenticalSamplesReproState),
    [selectedIdenticalSamplesReproState],
  );
  const projectSamplesError = useMemo(() => {
    if (projects?.find(p => p.id === targetProjectId)?.type === ProjectType.POOLED) {
      return 'Pooled projects not supported';
    }
    if (!targetProjectId) {
      return `Please select a project to ${action.toLowerCase()} the samples ${action === ProjectSampleAction.REMOVE ? 'from' : 'to'}`;
    }
    if (!selectedSamples.length) {
      return `Please select some samples to ${action.toLowerCase()}`;
    }
    return null;
  }, [action, targetProjectId, selectedSamples, projects]);

  const changeSampleProjects = useCallback(
    async (accessStrategy: ProjectSamplesAccessStrategy, confirmProceedWithPartialVolumeNormalisation = false) => {
      if (!targetProjectId) {
        showToastError(
          'Unable to manage samples',
          'Please select a target project before changing sample memberships.',
        );
        return;
      }

      try {
        await adminFeatures.patchSamplesToProject.mutateAsync({
          [`${targetProjectId}`]: {
            action,
            sampleIds: selectedSamples,
            sourceProjectId: project.id,
            accessStrategy,
            confirmProceedWithPartialVolumeNormalisation,
          },
        });
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 409) {
          const parsed = zPartialVolumeNormalisationConfirmation.safeParse(error.response.data);
          if (parsed.success) {
            setPartialVolumeConfirmation({
              accessStrategy,
              payload: parsed.data,
            });
            return;
          }
        }
        showToastError(
          `${action} samples ${action === ProjectSampleAction.REMOVE ? 'from' : 'to'} project failed!`,
          error instanceof Error ? error.message : 'Unknown error prevented to manage samples',
        );
        return;
      }

      showToast({
        title: `Samples (${action}) to project`,
        description:
          'Please notice that you might need to reload the page with default selections (e.g. if you have sampling points selected that were removed from the project)',
        status: 'success',
        duration: 15000,
      });

      setSampleAccessModal(null);
      setPartialVolumeConfirmation(null);
    },
    [
      action,
      targetProjectId,
      selectedSamples,
      adminFeatures.patchSamplesToProject,
      showToastError,
      showToast,
      project.id,
      setPartialVolumeConfirmation,
    ],
  );

  if (!isAdmin(user)) {
    return null;
  }

  async function changeAdminLevels(levels: LevelOption[]) {
    await utilsChangeAdminLevels(levels, patchLevels.mutateAsync);
  }

  async function manageSamplesInProjects() {
    try {
      if (projectSamplesError || !targetProjectId) {
        showToast({
          title: 'Error in the form to add/move/remove samples!',
          description: projectSamplesError ?? 'Unknown error prevented to manage samples',
          status: 'error',
        });
        return;
      }

      const selectedCount = selectedSamples.length;
      const sampleNoun = selectedCount === 1 ? 'sample' : 'samples';

      if (action === ProjectSampleAction.REMOVE) {
        setSampleAccessModal({
          title: `Remove ${selectedCount} ${sampleNoun}`,
          message: (
            <Box>
              <Text>
                You are about to remove {selectedCount} {sampleNoun} from this project.
              </Text>
              <Text mt={2} fontWeight="bold" color="red.500">
                This action cannot be undone.
              </Text>
            </Box>
          ),
          options: [
            {
              key: 'remove-keep-access',
              label: 'Remove samples only',
              description: 'Keep sampling point and sample permissions unchanged.',
              strategy: ProjectSamplesAccessStrategy.LEAVE_UNCHANGED,
              variant: 'outline',
            },
            {
              key: 'remove-revoke-access',
              label: 'Remove samples and revoke orphan sampling access',
              description:
                'Revoke sampling point and sample permissions for organisations that no longer see these samples in any project.',
              strategy: ProjectSamplesAccessStrategy.REVOKE_ORPHAN_ACCESS,
              colorScheme: 'red',
            },
          ],
        });
        return;
      }

      if (!actionProjectId) {
        showToast({
          title: 'Select a target project',
          description: 'Please pick a project before adding or moving samples.',
          status: 'error',
        });
        return;
      }

      const { usersOnlyInTarget, usersOnlyInCurrent, orgsOnlyInTarget, orgsOnlyInCurrent } =
        adminFeatures.getAccessDifferences(actionProjectId);

      const targetProject = projects?.find(p => p.id === actionProjectId);
      const targetProjectName = targetProject?.name ?? `project ${actionProjectId}`;

      const summaryItems: string[] = [];
      if (orgsOnlyInTarget.length) {
        summaryItems.push(
          `${orgsOnlyInTarget.length} organisation${orgsOnlyInTarget.length === 1 ? '' : 's'} can see ${targetProjectName} but not ${project.name}.`,
        );
      }
      if (orgsOnlyInCurrent.length) {
        summaryItems.push(
          `${orgsOnlyInCurrent.length} organisation${orgsOnlyInCurrent.length === 1 ? '' : 's'} only see ${project.name}.`,
        );
      }
      if (usersOnlyInTarget.length) {
        summaryItems.push(
          `${usersOnlyInTarget.length} user${usersOnlyInTarget.length === 1 ? '' : 's'} belong exclusively to ${targetProjectName}.`,
        );
      }
      if (usersOnlyInCurrent.length) {
        summaryItems.push(
          `${usersOnlyInCurrent.length} user${usersOnlyInCurrent.length === 1 ? '' : 's'} belong exclusively to ${project.name}.`,
        );
      }

      const accessDifferenceMessage = (
        <Box>
          <Text>
            {action === ProjectSampleAction.ADD
              ? `Add ${selectedCount} ${sampleNoun} from ${project.name} to ${targetProjectName}.`
              : `Move ${selectedCount} ${sampleNoun} from ${project.name} to ${targetProjectName}.`}
          </Text>
          {summaryItems.length ? (
            <UnorderedList mt={3} pl={4} spacing={1}>
              {summaryItems.map(item => (
                <ListItem key={item}>{item}</ListItem>
              ))}
            </UnorderedList>
          ) : (
            <Text mt={3} color="gray.600">
              No access differences detected between these projects.
            </Text>
          )}
        </Box>
      );

      if (action === ProjectSampleAction.ADD) {
        const orgsOnlyInTargetCount = orgsOnlyInTarget.length;
        const grantDescription = orgsOnlyInTargetCount
          ? `${orgsOnlyInTargetCount} organisation${orgsOnlyInTargetCount === 1 ? '' : 's'} that already see ${targetProjectName} will be granted sampling access.`
          : 'No additional organisations currently require new sampling access.';

        setSampleAccessModal({
          title: `Add ${selectedCount} ${sampleNoun} to ${targetProjectName}`,
          message: accessDifferenceMessage,
          options: [
            {
              key: 'add-grant',
              label: 'Grant sampling access to target organisations',
              description: grantDescription,
              strategy: ProjectSamplesAccessStrategy.GRANT_TARGET_ORGANISATIONS,
              colorScheme: 'blue',
            },
            {
              key: 'add-unchanged',
              label: 'Add samples only',
              description: 'Keep sampling point and sample permissions unchanged.',
              strategy: ProjectSamplesAccessStrategy.LEAVE_UNCHANGED,
              variant: 'outline',
            },
          ],
        });
        return;
      }

      const moveOrgsOnlyInTargetCount = orgsOnlyInTarget.length;
      const moveOrgsOnlyInCurrentCount = orgsOnlyInCurrent.length;
      const parts: string[] = [];
      if (moveOrgsOnlyInTargetCount) {
        parts.push(
          `${moveOrgsOnlyInTargetCount} organisation${moveOrgsOnlyInTargetCount === 1 ? '' : 's'} from ${targetProjectName} will gain sampling access.`,
        );
      }
      if (moveOrgsOnlyInCurrentCount) {
        parts.push(
          `${moveOrgsOnlyInCurrentCount} organisation${moveOrgsOnlyInCurrentCount === 1 ? '' : 's'} that only see ${project.name} will have sampling access revoked.`,
        );
      }

      setSampleAccessModal({
        title: `Move ${selectedCount} ${sampleNoun} to ${targetProjectName}`,
        message: accessDifferenceMessage,
        options: [
          {
            key: 'move-sync',
            label: 'Grant to target organisations and revoke from source-only organisations',
            description: parts.length ? parts.join(' ') : 'No organisations currently require access changes.',
            strategy: ProjectSamplesAccessStrategy.SYNC_BETWEEN_PROJECTS,
            colorScheme: 'blue',
          },
          {
            key: 'move-unchanged',
            label: 'Move samples only',
            description: 'Keep sampling point and sample permissions unchanged.',
            strategy: ProjectSamplesAccessStrategy.LEAVE_UNCHANGED,
            variant: 'outline',
          },
        ],
      });
      return;

      await changeSampleProjects(ProjectSamplesAccessStrategy.LEAVE_UNCHANGED);
    } catch (error) {
      console.error('Error in manageSamplesInProjects:', error);
      showToastError('Error managing samples', error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  }

  return (
    <GridView>
      <RSecondTitle>Project Administration</RSecondTitle>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={oldTheme.spacing.m} width="100%" alignItems="start">
        <Flex minWidth={250} flexDirection="column">
          <RThirdTitle>Rename project</RThirdTitle>
          <Flex alignItems="center">
            <Input
              bg="white"
              value={projectName}
              onChange={event => {
                setProjectName(event.target.value);
              }}
            />
            <Button
              disabled={!!projectName || projectName === project.name}
              onClick={_event => {
                patchName.mutate(projectName);
              }}
            >
              Rename
            </Button>
          </Flex>
        </Flex>
        <Box minWidth={250}>
          <RThirdTitle>Organisation access</RThirdTitle>
          <UnorderedList style={{ listStyleType: 'disc' }}>
            {organisationsWithAccess.map(org => (
              <OrganisationAccessListItem
                key={org.id}
                organisation={org}
                project={project}
                organisationUsers={adminFeatures.organisationUsersByOrganisationId.get(org.id) ?? []}
                projectSampleAccessInfo={projectSampleAccessInfo}
              />
            ))}
            <ListItem>
              <AddOrganisationAccessDropdown
                organisations={adminFeatures.organisationsWithoutAccess}
                projectId={project.id}
              />
            </ListItem>
          </UnorderedList>
        </Box>
        <Box minWidth={200}>
          <RThirdTitle>Reproducibility</RThirdTitle>
          <Flex direction="column" gap={2} alignItems="flex-start">
            <Button
              as="a"
              href={reproducibilityHref}
              target="_blank"
              rel="noopener noreferrer"
              isDisabled={!selectedSamples.length}
            >
              Open Reproducibility
            </Button>
            <Button
              as="a"
              href={selectedIdenticalSamplesReproState ? selectedIdenticalSamplesReproHref : undefined}
              target="_blank"
              rel="noopener noreferrer"
              isDisabled={!selectedIdenticalSamplesReproState}
            >
              Open selected identical samples
            </Button>
          </Flex>
        </Box>
        {/* <Box minWidth={150} maxWidth={200}>
          <RThirdTitle>Extra analyses</RThirdTitle>
          <ul>
            {plotFileNames.map((filename, key) => {
              return (
                <li key={key}>
                  {filename}{' '}
                  <DeleteIcon
                    // eslint-disable-next-line @typescript-eslint/require-await
                    triggerDelete={async () => {
                      patchImages([], filename);
                    }}
                    name={filename}
                  />
                </li>
              );
            })}
          </ul>
          <div style={{ marginTop: -20, width: '80%' }}>
            <FileDropzone onDrop={onDrop} />
          </div>
        </Box> */}
        <Box>
          <RThirdTitle>Display Settings</RThirdTitle>

          {/* X-Axis mode */}
          <Box mb={4} width="100%" minWidth="250px">
            <Text mb={1} fontWeight="bold">
              Default X-Axis
            </Text>
            <ResistomapSelect<XAxisModeOption, false, GroupBase<XAxisModeOption>>
              options={xAxisModeOptions}
              chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
              value={xAxisModeOptions.find(option => option.value === project.defaultXAxisMode) || xAxisModeOptions[0]}
              onChange={(option: XAxisModeOption | null) => {
                if (option) {
                  patchXAxisMode.mutate({ xAxisMode: option.value });
                  showToast({
                    title: 'X-Axis Display Mode Updated',
                    description: 'The chart display setting has been updated.',
                    status: 'success',
                    duration: 5000,
                  });
                }
              }}
              placeholder="Select X-Axis display mode..."
            />
          </Box>

          {/* Administrative levels */}
          <Box mb={4} width="100%" minWidth="250px">
            <Text mb={1} fontWeight="bold">
              Administrative Levels
            </Text>
            <Flex alignItems="center">
              <ResistomapSelect<LevelOption, true, GroupBase<LevelOption>>
                options={allLevelsWithZoomableAreasOptions}
                chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
                defaultValue={levelsWithZoomableAreasOptions}
                isMulti
                onChange={options => {
                  setLevelsWithZoomableAreasOptions([...options]);
                }}
                placeholder="Select administrative levels..."
                closeMenuOnSelect={false}
              />
              <Button
                ml={2}
                onClick={() => void changeAdminLevels(levelsWithZoomableAreasOptions)}
                isLoading={patchLevels.isPending}
              >
                Change levels
              </Button>
            </Flex>
          </Box>

          {/* Highlight flag */}
          <Box width="100%" minWidth="250px">
            <Text mb={1} fontWeight="bold">
              Highlight Flag
            </Text>
            <ResistomapSelect<{ label: string; value: string }, false, GroupBase<{ label: string; value: string }>>
              options={flagOptions}
              chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
              value={
                flagOptions.find(o => o.value === String(adminFeatures.highlightFlag ?? '')) ||
                flagOptions[flagOptions.length - 1]
              }
              onChange={option => {
                adminFeatures.setHighlightFlag(option && option.value ? (option.value as SmartChipFlag) : null);
              }}
              placeholder="Highlight flag..."
            />
          </Box>
        </Box>
        <Box>
          <RThirdTitle>Danger zone</RThirdTitle>
          <Flex direction="row" alignItems="center">
            <Button
              leftIcon={<FontAwesomeIcon icon={faTrashAlt} />}
              colorScheme="red"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
            >
              Delete project
            </Button>
          </Flex>
          <Text mt={2} color="gray.600">
            Project deletion keeps samples and sampling points intact so they can be reassigned later.
          </Text>
        </Box>

        {/* Sample management section */}
        <Box minWidth={250} width="100%">
          <RThirdTitle>Sample Management</RThirdTitle>
          <SampleManagementForm
            project={project}
            action={action}
            setAction={setAction}
            actionProjectId={actionProjectId}
            setActionProjectId={setActionProjectId}
            pristineProjectSamples={pristineProjectSamples}
            setPristineProjectSamples={setPristineProjectSamples}
            projectSamplesError={projectSamplesError}
            onManageSamples={() => void manageSamplesInProjects()}
            isLoading={adminFeatures.patchSamplesToProject.isPending}
          />
        </Box>
      </SimpleGrid>

      {sampleAccessModal ? (
        <MultiOptionConfirmModal
          isOpen
          onClose={() => {
            setSampleAccessModal(null);
          }}
          title={sampleAccessModal.title}
          message={sampleAccessModal.message}
          options={sampleAccessModal.options.map(option => ({
            key: option.key,
            label: option.label,
            description: option.description,
            colorScheme: option.colorScheme,
            variant: option.variant,
            onSelect: async () => {
              await changeSampleProjects(option.strategy);
            },
          }))}
        />
      ) : null}
      {partialVolumeConfirmation ? (
        <MultiOptionConfirmModal
          isOpen
          onClose={() => {
            setPartialVolumeConfirmation(null);
          }}
          title="Proceed with partial volume normalisation?"
          message={
            <Box>
              <Text>
                This change would create mixed filtered-volume completeness for water samples ({' '}
                {partialVolumeConfirmation.payload.withVolumeCount} with volume,{' '}
                {partialVolumeConfirmation.payload.withoutVolumeCount} without volume).
              </Text>
              <Text mt={3}>
                No volume-based results will be available for <b>any samples</b> within this project.
              </Text>
            </Box>
          }
          options={[
            {
              key: 'confirm-partial-volume',
              label: 'Proceed',
              colorScheme: 'orange',
              onSelect: async () => {
                await changeSampleProjects(partialVolumeConfirmation.accessStrategy, true);
              },
            },
          ]}
        />
      ) : null}
      <MultiOptionConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
        }}
        title={`Delete ${project.name}?`}
        message={
          totalProjectSamples > 0
            ? `${project.name} will be removed but ${projectSampleLabel} and ${samplingPointLabel} remain available for other projects. Choose how to handle sampling access.`
            : `${project.name} will be removed. Samples and sampling points remain in the system.`
        }
        options={[
          {
            key: 'shallow-keep',
            label: 'Shallow delete and keep sampling access',
            variant: 'outline',
            colorScheme: 'blue',
            description: `Remove the project shell but keep ${projectSampleLabel} and ${samplingPointLabel} accessible to organisations.`,
            onSelect: async () => {
              await triggerDelete('shallow-keep');
            },
          },
          {
            key: 'shallow-revoke',
            label: 'Shallow delete and revoke sampling access',
            variant: 'outline',
            colorScheme: 'orange',
            description: 'Remove the project shell and revoke sampling point/sample access granted via this project.',
            onSelect: async () => {
              await triggerDelete('shallow-revoke');
            },
          },
          // Deep deletion previously allowed removing samples and sampling points. Retained for potential re-enable:
          // {
          //   key: 'deep',
          //   label: 'Deep delete (project and data)',
          //   colorScheme: 'red',
          //   description: 'Permanently delete the project, its samples, and related sampling points when unused elsewhere.',
          //   onSelect: async () => {
          //     await triggerDelete('deep');
          //   },
          // },
        ]}
      />
    </GridView>
  );
}

interface XAxisModeOption {
  value: XAxisMode;
  label: string;
}

function getXAxisModeLabel(mode: XAxisMode): string {
  switch (mode) {
    case XAxisMode.TIME:
      return 'Against time';
    case XAxisMode.ORDINAL_GROUPED:
      return 'Ordinal (group bio reps)';
    case XAxisMode.ORDINAL_INDIVIDUAL:
      return 'Ordinal (samples individually)';
    default:
      return 'Unknown';
  }
}
